import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Copy, Check, ExternalLink, FileText } from "lucide-react";
import { toast } from "sonner";
import { RevealLines } from "./motion";
import { PriceTicker } from "./PriceTicker";
import { ShareCard } from "./ShareCard";
import { TOKEN, LINKS, IMAGES } from "@/lib/site";

const getRemaining = (target) => {
  const diff = Math.max(0, target - Date.now());
  return {
    days: Math.floor(diff / 86400000),
    hours: Math.floor((diff % 86400000) / 3600000),
    mins: Math.floor((diff % 3600000) / 60000),
    secs: Math.floor((diff % 60000) / 1000),
  };
};

const BigUnit = ({ v, l }) => (
  <div className="flex flex-col items-center" data-testid={`hero-countdown-${l.toLowerCase()}`}>
    <span className="font-mono font-bold text-5xl sm:text-7xl lg:text-8xl text-white tabular-nums leading-none">
      {String(v).padStart(2, "0")}
    </span>
    <span className="font-mono text-[10px] sm:text-xs uppercase tracking-[0.3em] text-white/40 mt-3">{l}</span>
  </div>
);

const Sep = () => (
  <span className="font-mono font-bold text-4xl sm:text-6xl lg:text-7xl text-[#ff007f] leading-none self-start mt-1 sm:mt-2">:</span>
);

export const Hero = () => {
  const ref = useRef(null);
  const [copied, setCopied] = useState(false);
  const target = new Date(TOKEN.countdownISO).getTime();
  const [t, setT] = useState(getRemaining(target));

  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const overlay = useTransform(scrollYProgress, [0, 1], [0.62, 0.9]);

  useEffect(() => {
    const id = setInterval(() => setT(getRemaining(target)), 1000);
    return () => clearInterval(id);
  }, [target]);

  const copy = () => {
    navigator.clipboard.writeText(TOKEN.contract);
    setCopied(true);
    toast.success("Contract copied. Now go pump. 🐔");
    setTimeout(() => setCopied(false), 1800);
  };

  return (
    <section id="top" ref={ref} className="relative min-h-screen flex items-center overflow-hidden pt-28 pb-20" data-testid="hero-section">
      {/* Parallax background */}
      <motion.div style={{ y, scale }} className="absolute inset-0 z-0">
        <img src={IMAGES.mascot} alt="Screaming Chicken mascot" className="w-full h-full object-cover object-center" />
      </motion.div>
      <motion.div style={{ opacity: overlay }} className="absolute inset-0 z-10 bg-black" />
      <div className="absolute inset-0 z-10 bg-gradient-to-t from-[#050505] via-transparent to-[#050505]/60" />

      <div className="relative z-20 max-w-7xl mx-auto px-5 sm:px-6 w-full">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.3em] text-[#39ff14] border border-[#39ff14]/40 rounded-full px-4 py-1.5 mb-6"
        >
          <span className="w-1.5 h-1.5 rounded-full bg-[#39ff14] animate-pulse" />
          White Paper Sealed &middot; Solana
        </motion.div>

        <RevealLines
          className="font-street uppercase tracking-tight leading-[0.95] text-3xl sm:text-5xl lg:text-6xl"
          lines={["Screaming Chicken"]}
          delay={0.35}
        />
        <RevealLines
          className="font-display font-bold uppercase tracking-tight leading-none text-sm sm:text-lg lg:text-xl mt-3 hero-gradient-text"
          lines={["$AHHH — Homelessness Reform Society"]}
          delay={0.6}
        />

        {/* CENTERPIECE COUNTDOWN */}
        <motion.div
          id="whitepaper"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.85, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="mt-10 sm:mt-12"
          data-testid="hero-countdown"
        >
          <div className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.3em] text-[#00f0ff] mb-5">
            <FileText className="w-4 h-4" />
            White Paper Unlocks In
            <span className="text-white/30">/ Jan 29, 2027 · 00:00 UTC</span>
          </div>
          <div className="flex items-start gap-3 sm:gap-6">
            <BigUnit v={t.days} l="Days" />
            <Sep />
            <BigUnit v={t.hours} l="Hours" />
            <Sep />
            <BigUnit v={t.mins} l="Mins" />
            <Sep />
            <BigUnit v={t.secs} l="Secs" />
          </div>
          <ShareCard />
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.1 }}
          className="font-mono text-sm text-white/60 max-w-lg mt-8 leading-relaxed"
        >
          A meme with a mission is being built for the ones the system forgot. The blueprint
          is sealed. <span className="text-[#ffb800]">The clock is ticking.</span>
        </motion.p>

        <PriceTicker />

        {/* Contract + CTAs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.25 }}
          className="mt-6 flex flex-col gap-3 max-w-2xl"
        >
          <button
            onClick={copy}
            data-testid="hero-copy-contract"
            className="group flex items-center justify-between gap-3 font-mono text-xs sm:text-sm bg-black/60 backdrop-blur-md border border-white/15 hover:border-[#00f0ff] rounded-lg px-4 py-3.5 transition-colors"
          >
            <span className="truncate text-white/80">{TOKEN.contract}</span>
            {copied ? <Check className="w-4 h-4 text-[#39ff14] shrink-0" /> : <Copy className="w-4 h-4 text-white/50 group-hover:text-[#00f0ff] shrink-0" />}
          </button>
          <div className="flex flex-wrap gap-3">
            <a href={LINKS.pumpfun} target="_blank" rel="noopener noreferrer" data-testid="hero-buy-btn"
              className="font-display font-bold text-sm px-7 py-3.5 rounded-full bg-[#ff007f] text-black hover:bg-[#ffb800] transition-colors glow-magenta">
              BUY SCREAMING CHICKEN
            </a>
            <a href={LINKS.dexscreener} target="_blank" rel="noopener noreferrer" data-testid="hero-dexscreener-btn"
              className="font-display font-bold text-sm px-7 py-3.5 rounded-full border border-[#00f0ff]/50 text-[#00f0ff] hover:bg-[#00f0ff] hover:text-black transition-colors inline-flex items-center gap-2">
              DEXSCREENER <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
